/**
 * What Orik reads from a native session window itself.
 *
 * The transcript is the harness's own conversation panel; the one thing Orik still reads out of
 * the log is its own durable delivery receipt ({@link deliveryFrom}), which the bridge and the
 * channel turn both turn into file parts.
 *
 * The harness's types are declared structurally rather than imported. This module is bundled
 * into the browser artifact (`harness/orik-client.ts`), and the shape it reads is a wire
 * contract between two processes.
 */

/** One assembled content block, as the harness writes it into the log. */
export interface SessionTextBlock {
  readonly type: "text";
  readonly text: string;
}
export interface SessionReasoningBlock {
  readonly type: "reasoning";
  readonly text: string;
}
export interface SessionToolCallBlock {
  readonly type: "tool-call";
  readonly id: string;
  readonly name: string;
  readonly arguments: string;
}
export interface SessionToolResultBlock {
  readonly type: "tool-result";
  readonly toolCallId: string;
  readonly content: readonly SessionBlock[];
  readonly isError?: boolean;
}
/**
 * The blocks this bridge reads.
 *
 * Deliberately a closed union: the harness's block map is merge-extensible, so a deployment
 * can add a type, and a block this does not know simply matches no branch below rather than
 * widening what the known ones mean. An index signature here would do the opposite — it makes
 * `block.text` unknown on every arm.
 */
export type SessionBlock = SessionTextBlock | SessionReasoningBlock | SessionToolCallBlock | SessionToolResultBlock;

/** One durable session event: `{ type, seq, time, data }`, with `data` shaped by `type`. */
export interface SessionEventLike {
  readonly type: string;
  readonly seq?: number;
  readonly time?: number;
  readonly data?: unknown;
}

/** One client-only live chunk: the model stream for the step that is still running. */
export interface SessionLiveChunk {
  readonly type: "assistant/live-chunk";
  readonly data?: { readonly turn?: unknown; readonly step?: unknown; readonly chunk?: SessionStreamChunk };
}

/** One model stream chunk, correlated to its block by index, as the agent loop publishes it. */
export type SessionStreamChunk =
  | { readonly type: "block-start"; readonly index: number; readonly blockType: string }
  | { readonly type: "text-delta" | "reasoning-delta"; readonly index: number; readonly text: string }
  | { readonly type: "tool-call-delta"; readonly index: number; readonly id: string; readonly name?: string; readonly argumentsDelta: string }
  | { readonly type: "block-end"; readonly index: number; readonly block: SessionBlock }
  | { readonly type: string; readonly index?: number };

/** One entry of a session's event window. */
export type SessionWindowEntry =
  | { readonly type: "event"; readonly event: SessionEventLike }
  | { readonly type: "transient"; readonly event: SessionLiveChunk };

/** The text of a block list, which is all the model-facing result carries as text. */
function textOf(blocks: readonly SessionBlock[] | undefined): string {
  if (blocks === undefined) return "";
  return blocks.filter(block => block.type === "text").map(block => String((block as { text?: unknown }).text ?? "")).join("");
}

/** Orik's durable delivery receipt, carried as ordinary native plugin context. */
export function deliveryFrom(event: SessionEventLike): { callId: string; turn: number; step: number; files: { url: string; mediaType: string }[] } | undefined {
  if (event.type !== "user/message") return;
  const data = event.data as { source?: { kind?: string; plugin?: string }; content?: SessionBlock[] } | undefined;
  // Session format v4 names the producer as the kind; v3 wrapped it in a `plugin` source.
  const delivery = data?.source?.kind === "plugin:atlas-delivery" || (data?.source?.kind === "plugin" && data.source.plugin === "atlas-delivery");
  if (!delivery) return;
  try {
    const value = JSON.parse(textOf(data.content)) as { callId?: unknown; turn?: unknown; step?: unknown; files?: unknown };
    if (typeof value.callId !== "string" || typeof value.turn !== "number" || !Number.isSafeInteger(value.turn) || value.turn < 0 || typeof value.step !== "number" || !Number.isSafeInteger(value.step) || value.step < 0 || !Array.isArray(value.files)) return;
    const files = value.files.filter((file): file is { url: string; mediaType: string } =>
      file !== null && typeof file === "object" && typeof file.url === "string" && typeof file.mediaType === "string" && /^\/api\/conversations\/[^/]+\/files\/[^/?]+(?:\?filename=[^#]*)?$/.test(file.url));
    return files.length === 0 ? undefined : { callId: value.callId, turn: value.turn, step: value.step, files };
  } catch { return; }
}
